// BotAdmin is the "eRecords On-Call bot" part of Admin > Microsoft Teams: the
// order and timing people are reached in (Teams message, Teams call, SMS,
// phone call), a test for anyone, who has the bot chat, and the Teams app
// package to upload.
import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ChevronRight,
  ContentCopy,
  Download,
  Forum,
  Phone,
  PhoneInTalk,
  Sms,
} from '@mui/icons-material'
import { UserAvatar } from '../util/avatars'
import { serif } from './brand'
import { BotStatus, NotifyDelays, botApi } from './api'
import { IconTile, useBrand } from './ui'

export const orderSteps: {
  key: keyof NotifyDelays
  label: string
  icon: React.ReactNode
  teams?: boolean
}[] = [
  { key: 'teamsChat', label: 'Teams message', icon: <Forum />, teams: true },
  { key: 'teamsCall', label: 'Teams call', icon: <PhoneInTalk />, teams: true },
  { key: 'sms', label: 'SMS', icon: <Sms /> },
  { key: 'voice', label: 'Phone call', icon: <Phone /> },
]

// orderText: "Teams message right away → Teams call after 1 min → …"
export function orderText(d: NotifyDelays, teams: boolean): string {
  return orderSteps
    .filter((s) => teams || !s.teams)
    .map(
      (s) => `${s.label} ${d[s.key] ? `after ${d[s.key]} min` : 'right away'}`,
    )
    .join(' → ')
}

function SectionTitle(props: {
  title: string
  hint?: React.ReactNode
}): React.JSX.Element {
  const c = useBrand()
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontWeight: 600, color: c.ink }}>
        {props.title}
      </Typography>
      {props.hint && (
        <Typography sx={{ fontSize: 13, color: c.muted, mt: 0.25 }}>
          {props.hint}
        </Typography>
      )}
    </Box>
  )
}

export default function BotAdmin(): React.JSX.Element {
  const c = useBrand()
  const [st, setSt] = useState<BotStatus>()
  const [d, setD] = useState<NotifyDelays>()
  const [err, setErr] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [who, setWho] = useState('')
  const [copied, setCopied] = useState(false)

  const load = useCallback(() => {
    botApi
      .status()
      .then((s) => {
        setSt(s)
        setD(s.delays)
      })
      .catch((e: Error) => setErr(e.message))
  }, [])
  useEffect(load, [load])

  const run = (fn: () => Promise<unknown>, done: string): void => {
    setBusy(true)
    setErr('')
    setNote('')
    fn()
      .then(() => {
        setNote(done)
        load()
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false))
  }

  if (!st || !d) {
    return (
      <Card sx={{ p: 2.5 }}>
        <Typography sx={{ color: c.muted }}>{err || 'Loading…'}</Typography>
      </Card>
    )
  }

  const changed = orderSteps.some((s) => d[s.key] !== st.delays[s.key])
  const ordered = orderSteps.every(
    (s, i) => i === 0 || d[s.key] >= d[orderSteps[i - 1].key],
  )
  const ready = st.people.filter((p) => p.chatReady).length

  return (
    <Card sx={{ p: 0, overflow: 'hidden' }}>
      {/* header */}
      <Box sx={{ p: 2.5, pb: 2, display: 'flex', gap: 2, alignItems: 'start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            component='h2'
            sx={{ fontFamily: serif, fontSize: 20, color: c.ink }}
          >
            eRecords On-Call bot
          </Typography>
          <Typography sx={{ fontSize: 13, color: c.muted, mt: 0.25 }}>
            Each person on call gets the alert as a Teams message with
            Acknowledge, Escalate and Close. If nobody responds, the bot calls
            them in Teams, then SMS and a phone call follow.
          </Typography>
        </Box>
        <Chip
          size='small'
          variant='outlined'
          color={st.configured ? 'success' : 'default'}
          label={st.configured ? 'Connected' : 'Not configured'}
        />
      </Box>

      {(err || note || !st.configured) && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          {!st.configured && (
            <Alert severity='info'>
              Set ERECORDS_TEAMS_BOT_APP_ID and ERECORDS_TEAMS_BOT_SECRET (the
              “eRecords On-Call” Entra app) to turn on Teams messages and calls.
              Until then SMS and phone calls go out right away.
            </Alert>
          )}
          {err && <Alert severity='error'>{err}</Alert>}
          {note && (
            <Alert severity='success' onClose={() => setNote('')}>
              {note}
            </Alert>
          )}
        </Box>
      )}

      <Divider />

      {/* notification order */}
      <Box sx={{ p: 2.5 }}>
        <SectionTitle
          title='Notification order'
          hint="Minutes after a person's escalation step starts. Everything stops once someone acknowledges."
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.5,
          }}
        >
          {orderSteps.map((s, i) => {
            const off = !st.configured && s.teams
            return (
              <Box
                key={s.key}
                sx={{
                  position: 'relative',
                  border: `1px solid ${c.line}`,
                  borderRadius: '14px',
                  p: 1.5,
                  bgcolor: c.surface,
                  opacity: off ? 0.5 : 1,
                }}
              >
                {i > 0 && (
                  <ChevronRight
                    aria-hidden
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      left: -16,
                      top: 'calc(50% - 10px)',
                      fontSize: 20,
                      color: c.muted,
                    }}
                  />
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    mb: 1.25,
                  }}
                >
                  <IconTile tone='muted'>{s.icon}</IconTile>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: c.muted }} noWrap>
                      Step {i + 1} ·{' '}
                      {d[s.key] ? `after ${d[s.key]} min` : 'right away'}
                    </Typography>
                  </Box>
                </Box>
                <TextField
                  size='small'
                  type='number'
                  fullWidth
                  disabled={off}
                  value={d[s.key]}
                  onChange={(e) =>
                    setD({
                      ...d,
                      [s.key]: Math.max(
                        0,
                        Math.min(120, Number(e.target.value) || 0),
                      ),
                    })
                  }
                  inputProps={{
                    min: 0,
                    max: 120,
                    'aria-label': `${s.label} after minutes`,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>after</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position='end'>min</InputAdornment>
                    ),
                  }}
                />
              </Box>
            )
          })}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mt: 1.5,
            minHeight: 36,
          }}
        >
          <Typography sx={{ fontSize: 13, color: ordered ? c.muted : c.warn }}>
            {ordered
              ? orderText(d, st.configured)
              : 'Out of order: people may be called before they get the message.'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {changed && (
            <React.Fragment>
              <Button onClick={() => setD(st.delays)} disabled={busy}>
                Undo
              </Button>
              <Button
                variant='contained'
                disabled={busy}
                onClick={() =>
                  run(
                    () => botApi.setDelays(d),
                    'Order saved and applied to everyone.',
                  )
                }
              >
                Save order
              </Button>
            </React.Fragment>
          )}
        </Box>
      </Box>

      {st.configured && (
        <React.Fragment>
          <Divider />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            {/* try it */}
            <Box
              sx={{
                p: 2.5,
                borderRight: { md: `1px solid ${c.line}` },
                borderBottom: { xs: `1px solid ${c.line}`, md: 'none' },
              }}
            >
              <SectionTitle
                title='Try it'
                hint='Sends a sample card or rings the person in Teams. Nothing is recorded.'
              />
              <TextField
                select
                size='small'
                fullWidth
                label='Person'
                value={who}
                onChange={(e) => setWho(e.target.value)}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1.5 }}
              >
                <MenuItem value=''>Me</MenuItem>
                {st.people.map((p) => (
                  <MenuItem key={p.userID} value={p.userID}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant='outlined'
                  startIcon={<Forum />}
                  disabled={busy}
                  onClick={() =>
                    run(
                      () => botApi.test('chat', who),
                      'Test message sent to the eRecords On-Call chat.',
                    )
                  }
                >
                  Send test message
                </Button>
                <Button
                  variant='outlined'
                  startIcon={<PhoneInTalk />}
                  disabled={busy}
                  onClick={() =>
                    run(() => botApi.test('call', who), 'Calling in Teams now.')
                  }
                >
                  Test call
                </Button>
              </Box>
            </Box>

            {/* people */}
            <Box sx={{ p: 2.5 }}>
              <SectionTitle
                title='People'
                hint={`${st.people.length} from the Entra sync · ${ready} with the bot chat ready`}
              />
              {st.people.length === 0 && (
                <Typography sx={{ fontSize: 13, color: c.muted }}>
                  Nobody yet. Run the Entra sync.
                </Typography>
              )}
              <Box sx={{ display: 'grid', gap: 1 }}>
                {st.people.map((p) => (
                  <Box
                    key={p.userID}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
                  >
                    <UserAvatar
                      userID={p.userID}
                      sx={{ width: 28, height: 28 }}
                    />
                    <Typography sx={{ fontSize: 14, flex: 1 }} noWrap>
                      {p.name}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        fontSize: 12,
                        color: p.chatReady ? c.ok : c.muted,
                      }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: p.chatReady ? c.ok : c.line,
                        }}
                      />
                      {p.chatReady
                        ? 'Chat ready'
                        : 'Gets the app on first alert'}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Divider />
          {/* teams app */}
          <Box
            sx={{
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>Teams app</Typography>
                <Chip
                  size='small'
                  variant='outlined'
                  color={st.catalogId ? 'success' : 'warning'}
                  label={st.catalogId ? 'In org catalog' : 'Not uploaded'}
                />
              </Box>
              <Typography sx={{ fontSize: 13, color: c.muted, mt: 0.25 }}>
                {st.catalogId
                  ? 'To update it: Teams admin center → Manage apps → eRecords On-Call → Upload file.'
                  : 'Upload it in Teams: Apps → Manage your apps → Upload an app → Upload an app to your org’s app catalog.'}
              </Typography>
            </Box>
            <Button
              variant='outlined'
              startIcon={<Download />}
              href={botApi.packageURL}
            >
              Download package
            </Button>
          </Box>
          <Box
            sx={{
              px: 2.5,
              pb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography sx={{ fontSize: 12, color: c.muted }} noWrap>
              Bot messaging endpoint (Azure Bot → Configuration):{' '}
              <code>{st.messagingEndpoint}</code>
            </Typography>
            <Tooltip title={copied ? 'Copied' : 'Copy'}>
              <IconButton
                size='small'
                aria-label='Copy the messaging endpoint'
                onClick={() =>
                  navigator.clipboard
                    .writeText(st.messagingEndpoint)
                    .then(() => {
                      setCopied(true)
                      setTimeout(() => setCopied(false), 1500)
                    })
                }
              >
                <ContentCopy sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </React.Fragment>
      )}
    </Card>
  )
}
